import { useMemo, useState } from 'react';

type SortDirection = 'ascending' | 'descending';

type SortDescriptor<SortKey extends string> = {
    column: SortKey;
    direction: SortDirection;
};

type SortValue = string | number | boolean | null | undefined;

type SortValueGetters<Item, SortKey extends string> = Record<SortKey, (item: Item) => SortValue>;

type UseSortableTableOptions<Item, SortKey extends string> = {
    items: Item[];
    initialSortDescriptor: SortDescriptor<SortKey>;
    sortValueGetters: SortValueGetters<Item, SortKey>;
};

const compareStrings = (firstValue: string, secondValue: string, direction: SortDirection) => {
    const result = firstValue.localeCompare(secondValue, undefined, { numeric: true });
    return direction === 'ascending' ? result : -result;
};

const getSortableString = (value: SortValue) => {
    if (value === null || value === undefined) return '';

    return String(value);
};

/**
 * Keeps DataTable sorting state and sorted rows together.
 */
const useSortableTable = <Item, SortKey extends string>(options: UseSortableTableOptions<Item, SortKey>) => {
    const { items, initialSortDescriptor, sortValueGetters } = options;

    const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor<SortKey>>(initialSortDescriptor);

    const sortedItems = useMemo(() => {
        const sortKey = sortDescriptor.column ?? initialSortDescriptor.column;
        const sortDirection = sortDescriptor.direction ?? initialSortDescriptor.direction;

        if (!sortKey) return items;

        const getSortValue = sortValueGetters[sortKey];
        if (!getSortValue) return items;

        return [...items].sort((firstItem, secondItem) =>
            compareStrings(
                getSortableString(getSortValue(firstItem)),
                getSortableString(getSortValue(secondItem)),
                sortDirection,
            ),
        );
    }, [initialSortDescriptor.column, initialSortDescriptor.direction, items, sortDescriptor, sortValueGetters]);

    return {
        sortedItems,
        sortDescriptor,
        setSortDescriptor,
    };
};

export { useSortableTable };
export type { SortDescriptor, SortDirection };
